import React, { FunctionComponent } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { ListButtonsContainer, QueryLoaderWrapper, Text } from '@companion-professional/components';
import { Page } from '../../components/Page';
import { useCheckupRequest } from '../../hooks/useCheckupRequest';
import { useClinicSurveysForCheckups } from '../../hooks/useClinicSurveysForCheckups';
import { getSurveyPathForCheckup } from '../../lib/surveyPath';
import { PageNotFound } from '../PageNotFound';
import { SurveyListItem } from '../Checkup/SurveyListItem';

type GroundTruthParams = {
  checkupId: string;
};

// GroundTruth is the landing page for links from outside vPortal that ask a user to record the ground truth (i.e. fill
// out a health survey) for a checkup. Nothing inside vPortal links here. If the clinic has exactly one survey, the user
// is sent straight to it; otherwise they pick which survey to fill out. A survey that has already been answered for the
// checkup opens the saved answers instead of a new survey.
//
// The checkup request is loaded immediately shown because it is what triggers the clinic switch prompt when the user is
// logged into a different clinic than the checkup's.
export const GroundTruth: FunctionComponent = () => {
  const { checkupId } = useParams<GroundTruthParams>();
  const { isPending: isPendingCheckup, error: errorCheckup, checkupRequest } = useCheckupRequest(checkupId || '');
  const { surveys, error: errorSurveys, isPending: isPendingSurveys } = useClinicSurveysForCheckups();

  if (!checkupId) {
    return <PageNotFound />;
  }

  // replace keeps this page out of the history, so the back button on the survey doesn't bounce the user straight back
  // into the survey again.
  const onlySurvey = surveys.length === 1 ? surveys[0] : undefined;
  if (checkupRequest && !isPendingSurveys && !errorSurveys && onlySurvey) {
    return <Navigate replace to={getSurveyPathForCheckup(onlySurvey.survey_id, checkupRequest)} />;
  }

  return (
    <Page title="Health Surveys">
      <QueryLoaderWrapper
        isPending={isPendingCheckup || isPendingSurveys}
        error={errorCheckup || errorSurveys}
        errorHeading="Error loading checkup surveys"
      >
        {checkupRequest && surveys.length > 0 ? (
          <>
            <Text className="mb-2">Select the survey to fill out for this checkup.</Text>
            <ListButtonsContainer>
              {surveys.map((survey) => (
                <SurveyListItem key={survey.survey_id} survey={survey} checkupRequest={checkupRequest} />
              ))}
            </ListButtonsContainer>
          </>
        ) : (
          <Text tone="muted">There are no health surveys available for this clinic.</Text>
        )}
      </QueryLoaderWrapper>
    </Page>
  );
};
